import { parseRole, type Role } from '@/domain/auth'
import {
    KNOWN_USER_CODES,
    UserError,
    type CreateUserInput,
    type UserAccount,
    type UserErrorCode,
} from '@/domain/users'
import type { UsersApi } from '@/ports/users-api'

import { appendActiveTenant } from './_active-tenant'

export class HttpUsersApi implements UsersApi {
    private readonly baseURL: string

    constructor(baseURL = '') {
        this.baseURL = baseURL.replace(/\/+$/, '')
    }

    async list(): Promise<UserAccount[]> {
        // Override tenant super_admin (topbar) — cf. _active-tenant.ts.
        const params = appendActiveTenant(new URLSearchParams())
        const qs = params.toString()
        const resp = await this.send(qs ? `/api/v1/users?${qs}` : '/api/v1/users', { method: 'GET' })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { items?: unknown }
        if (!Array.isArray(body.items)) {
            throw new UserError('internal_error', 'Invalid /users response shape')
        }
        return body.items.map(parseUser)
    }

    async create(input: CreateUserInput, csrfToken: string): Promise<UserAccount> {
        const payload: Record<string, unknown> = {
            email: input.email,
            name: input.name,
        }
        if (input.role) payload.role = input.role
        if (input.tenantId) payload.tenant_id = input.tenantId
        if (input.disabledLogin) payload.disabled_login = true
        const resp = await this.send('/api/v1/users', {
            method: 'POST',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify(payload),
        })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { user?: unknown }
        return parseUser(body.user)
    }

    async updateRole(id: string, role: Role, csrfToken: string): Promise<UserAccount> {
        const resp = await this.send(`/api/v1/users/${encodeURIComponent(id)}/role`, {
            method: 'PATCH',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify({ role }),
        })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { user?: unknown }
        return parseUser(body.user)
    }

    async updateDisabled(id: string, disabled: boolean, csrfToken: string): Promise<UserAccount> {
        const resp = await this.send(`/api/v1/users/${encodeURIComponent(id)}/disabled`, {
            method: 'PATCH',
            headers: csrfHeaders(csrfToken),
            body: JSON.stringify({ disabled }),
        })
        if (!resp.ok) throw await readError(resp)
        const body = (await resp.json()) as { user?: unknown }
        return parseUser(body.user)
    }

    private send(path: string, init: RequestInit): Promise<Response> {
        return fetch(this.baseURL + path, {
            ...init,
            credentials: 'include',
            cache: 'no-store',
        }).catch((cause: unknown) => {
            const message = cause instanceof Error ? cause.message : 'fetch failed'
            throw new UserError('network_error', message)
        })
    }
}

function csrfHeaders(token: string): Record<string, string> {
    return { 'Content-Type': 'application/json', 'X-CSRF-Token': token }
}

function parseUser(raw: unknown): UserAccount {
    if (!raw || typeof raw !== 'object') {
        throw new UserError('internal_error', 'Invalid user payload')
    }
    const obj = raw as Record<string, unknown>
    const role = parseRole(typeof obj.role === 'string' ? obj.role : null)
    if (!role) {
        throw new UserError('internal_error', `Invalid user role: ${String(obj.role)}`)
    }
    return {
        id: stringField(obj.id),
        tenantId: stringField(obj.tenant_id),
        email: stringField(obj.email),
        name: stringField(obj.name),
        role,
        disabled: typeof obj.disabled === 'boolean' ? obj.disabled : false,
        createdAt: stringField(obj.created_at),
        lastLogin: stringField(obj.last_login) || undefined,
    }
}

function stringField(v: unknown): string {
    return typeof v === 'string' ? v : ''
}

async function readError(resp: Response): Promise<UserError> {
    if (resp.status === 401) {
        return new UserError('unauthorized', 'Session expired', 401)
    }
    if (resp.status === 403) {
        try {
            const body = (await resp.json()) as { error?: { code?: unknown } }
            const code = typeof body.error?.code === 'string' ? body.error.code : ''
            if (
                code === 'csrf_required' ||
                code === 'csrf_invalid' ||
                code === 'tenant_scope' ||
                code === 'role_escalation'
            ) {
                return new UserError(code as UserErrorCode, code, 403)
            }
        } catch {
            // fallback
        }
        return new UserError('forbidden', 'Forbidden', 403)
    }
    let body: unknown = null
    try {
        body = await resp.json()
    } catch {
        return new UserError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const err = (body as { error?: { code?: unknown; message?: unknown } }).error
    if (!err || typeof err !== 'object') {
        return new UserError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const rawCode = typeof err.code === 'string' ? err.code : 'unknown'
    const code: UserErrorCode = (KNOWN_USER_CODES as readonly string[]).includes(rawCode)
        ? (rawCode as UserErrorCode)
        : 'unknown'
    const message = typeof err.message === 'string' ? err.message : `HTTP ${resp.status}`
    return new UserError(code, message, resp.status)
}
